<template>
  <layout-basic>
    <page-hero
      title="We'd Love to Hear From You"
      lead="Whether you have a question, need advice or would like a demonstration, we're here to help."
    />

    <section class="demo-video-section">
      <v-container style="max-width: 960px">
        <demo-video video-key="home" />
      </v-container>
    </section>


    <v-container>
      <section class="container-new pricing-section contact-form-section">
        <v-row>
          <v-col cols="12" md="6">
            <h2 class="font-sora contact-title">
              Need help choosing the right IMPROVE solution?
            </h2>
            <p class="contact-lead">
              Our team is here to help. You can also explore IMPROVE through our
              <router-link to="/watch-demo">4-minute demo</router-link> or
              <a :href="experienceHref" rel="noopener">start your free trial</a>.
            </p>
            <p class="mt-4 contact-detail">
              <img class="mr-2" src="static/icons/envelope.png" alt="" /><a
                class="fs-22"
                href="mailto:info@improve.online"
                >info@improve.online</a
              >
            </p>
          </v-col>
          <v-col cols="12" md="6">
            <div class="relative">
              <v-content>
                <v-card class="elevation-12 contact-us-form" fill-height>
                  <v-card-text>
                    <v-form ref="form" @submit.prevent="submitForm">
                      <!-- Honeypot: off-screen, invisible to real users. Bots that auto-fill every field are dropped server-side. -->
                      <input type="text" v-model="company_website" name="company_website" tabindex="-1" autocomplete="off" aria-hidden="true" style="position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;overflow:hidden;" />
                      <p>First Name:</p>
                      <v-text-field
                        v-model="first_name"
                        :rules="[rules.required, rules.name]"
                        label="First Name:"
                        type="text"
                        placeholder="John"
                        solo
                      ></v-text-field>
                      <p>Last Name:</p>
                      <v-text-field
                        v-model="last_name"
                        :rules="[rules.required, rules.name]"
                        label="Last Name:"
                        type="text"
                        placeholder="Smith"
                        solo
                      ></v-text-field>
                      <p>Company Name:</p>
                      <v-text-field
                        v-model="company_name"
                        :rules="[rules.required]"
                        label="Company Name"
                        placeholder="Smith Inc"
                        solo
                      ></v-text-field>
                      <p>Email Address:</p>
                      <v-text-field
                        v-model="email"
                        :rules="[rules.required, rules.email]"
                        label="Email"
                        placeholder="johnsmith@email.com"
                        solo
                        type="email"
                      ></v-text-field>
                      <p>Message:</p>
                      <v-textarea
                        v-model="messages"
                        :rules="[rules.required]"
                        label="Messages"
                        placeholder="Hi, Hello, Bonjour"
                        solo
                      ></v-textarea>

                      <div id="newsletter">
                        <v-checkbox
                          style="box-shadow: none !important;"
                          v-model="newsletter"
                          :label="
                            `Sign up for our newsletter to receive the latest news from IMPROVE.`
                          "
                        ></v-checkbox>
                      </div>
                      <div class="text-center mt-10">
                        <v-btn
                          :loading="loading"
                          class="demo-submit-btn"
                          type="submit"
                          depressed
                        >
                          Contact Us
                        </v-btn>
                      </div>
                    </v-form>
                  </v-card-text>
                </v-card>
              </v-content>
            </div>
          </v-col>
        </v-row>
      </section>
    </v-container>

    <!-- Trilogy Active on the support they get (16 Sep 2026), verbatim. -->
    <quote-row :quotes="pageQuotes" />

    <standard-cta
      title="Ready to transform employee learning?"
      lead="Watch the 4-minute demo or experience IMPROVE for yourself."
    />
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import AuthAPI from '@/Api/auth.js';
import PageHero from '@/components/marketing/PageHero.vue';
import StandardCta from '@/components/marketing/StandardCta.vue';
import QuoteRow from '@/components/marketing/QuoteRow.vue';
import { TRILOGY_SUPPORT } from '@/data/testimonials';
import DemoVideo from '@/components/DemoVideo.vue';
import { marketingPageMeta } from '@/utils/marketingPage';
import { getExperienceHref } from '@/config/ctas';

export default {
  name: 'Contact',
  computed: {
    experienceHref() {
      return getExperienceHref();
    },
  },
  components: {
    LayoutBasic,
    PageHero,
    QuoteRow,
    StandardCta,
    DemoVideo,
  },
  data: () => ({
    pageQuotes: [TRILOGY_SUPPORT],
    errorMessages: {},
    password: '',
    email: '',
    first_name: '',
    last_name: '',
    company_name: '',
    messages: '',
    newsletter: false,
    company_website: '',
    successMessage:
      'We have notified our team with your request. Some one from our team will be in touch shortly.',
    loading: false,
    rules: {
      required: (value) => !!value || 'Required.',
      email: (value) => {
        const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return pattern.test(value) || 'Invalid email.';
      },
      name: (value) =>
        (value && value.length >= 2) || 'Name must be at least 2 characters.',
    },
  }),
  metaInfo() {
    return marketingPageMeta({
      title: "We'd Love to Hear From You | IMPROVE",
      description:
        "Whether you have a question, need advice or would like a demonstration, we're here to help.",
      path: '/contact',
    });
  },
  methods: {
    submitForm() {
      if (this.$refs.form.validate()) {
        this.contactUs();
      }
    },
    contactUs() {
      this.loading = true;
      AuthAPI.contactUs({
        first_name: this.first_name,
        last_name: this.last_name,
        email: this.email,
        company_name: this.company_name,
        messages: this.messages,
        // Always a boolean. After $refs.form.reset() the checkbox model is
        // undefined, JSON.stringify drops the key, and the API 500s on the
        // missing array key - so the SECOND enquiry from a visitor was lost.
        newsletter: !!this.newsletter,
        company_website: this.company_website,
      })
        .then(() => {
          this.loading = false;
          // Clear the form so a second enquiry does not resend the first. Guarded
          // and BEFORE the toast: if the visitor navigated away mid-request the
          // component is gone, and an unguarded reset() here would throw into
          // .catch and show a failure toast for an enquiry that was delivered.
          if (this.$refs.form) this.$refs.form.reset();
          this.$snotify.success(this.successMessage);
        })
        .catch((err) => {
          this.valid = false;
          this.loading = false;
          // The visitor MUST be told. Before this, a 429 (rate limited) or a
          // 500 produced no visible change at all, so failures looked exactly
          // like success and enquiries were silently lost (2026-09-11 audit).
          const status = err && err.response && err.response.status;
          const msg =
            status === 429
              ? 'Too many requests right now. Please wait a few minutes and try again, or email info@improve.online.'
              : status === 422
              ? 'Some of your details were not accepted. Please shorten your message if it is very long, check your email address, and try again.'
              : 'Sorry, your message could not be sent. Please try again or email info@improve.online.';
          this.$snotify.error(msg);
          this.errorMessages = err && err.response ? this.$gf.errorResponse(err) : {};
        });
    },
  },
};
</script>

<style scoped>
.contact-lead {
  margin-top: 12px;
  font-size: 17px;
  line-height: 1.55;
  color: var(--brand-navy, #002458);
}

.contact-lead a {
  color: var(--brand-teal, #08a0a8);
  font-weight: 700;
  text-decoration: underline;
}

.contact-form-section {
  padding-bottom: 48px;
}

.contact-title {
  color: var(--brand-navy, #002458);
  font-size: 28px;
  line-height: 1.35;
}

.contact-detail a {
  color: var(--brand-navy, #002458);
  text-decoration: none;
}

.contact-detail a:hover {
  color: var(--brand-teal, #08a0a8);
}

.demo-submit-btn {
  background-color: var(--brand-navy, #002458) !important;
  color: #ffffff !important;
  text-transform: none !important;
  font-weight: 700 !important;
  font-size: 16px !important;
  letter-spacing: 0.02em !important;
  padding: 12px 36px !important;
  border-radius: 6px !important;
  min-height: 48px;
}

.demo-submit-btn:hover {
  background-color: var(--brand-teal, #08a0a8) !important;
}

</style>
